import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  VisualizationType,
  WidgetPreset,
  WidgetVizType,
} from 'src/app/core/models/widget.models';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { WidgetVisualizationTypeCardComponent } from '../widget-visualization-type-card/widget-visualization-type-card.component';
import { SuiTooltipDirective } from '@sui/tooltip';
import { HighlightPipe } from '../../../shared/pipes/highlight.pipe';

export interface WidgetPreviewEvent {
  widgetPreset: WidgetPreset;
  vizType: WidgetVizType;
}
@Component({
  selector: 'app-widget-preset-card',
  templateUrl: './widget-preset-card.component.html',
  styleUrls: ['./widget-preset-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    WidgetVisualizationTypeCardComponent,
    SuiTooltipDirective,
    HighlightPipe,
  ],
})
export class WidgetPresetCardComponent {
  @Input() widgetPreset: WidgetPreset;
  @Input() highlight: string = '';
  @Input() selectedPresetId: number = null;
  @Input() selectedChartTypeId: number = null;
  @Input() dataSourceObjectId: string;
  @Input() dataSourceObjectClassId: number;

  // ! Opt-in multi-select mode. When `selectable` is true the whole card
  // ! becomes a toggle button driven by `selected`, and the inner viz-type
  // ! icons become individually toggleable (driven by `selectedVizTypeIds`)
  // ! so a user can include multiple viz variations of the same preset.
  // ! Existing single-select call sites keep their behavior because
  // ! `selectable` defaults to false.
  @Input() selectable: boolean = false;
  @Input() selected: boolean = false;
  @Input() selectedVizTypeIds: number[] = [];

  @Output() onWidgetPreview: EventEmitter<WidgetPreviewEvent> =
    new EventEmitter();
  @Output() onWidgetCreate: EventEmitter<WidgetPreviewEvent> =
    new EventEmitter();
  @Output() selectionToggle: EventEmitter<WidgetPreset> = new EventEmitter();
  @Output() vizTypeToggle: EventEmitter<WidgetPreviewEvent> =
    new EventEmitter();

  constructor() {}

  VisualizationType = VisualizationType;

  previewWidget(vizType: WidgetVizType) {
    if (this.selectable) return;
    this.onWidgetPreview.emit({
      widgetPreset: this.widgetPreset,
      vizType: vizType,
    });
  }

  createWidget() {
    const vizType = this.widgetPreset.chart_types.find(
      (vizType) => vizType.id === this.selectedChartTypeId,
    );
    this.onWidgetCreate.emit({
      widgetPreset: this.widgetPreset,
      vizType: vizType,
    });
  }

  onCardClick(): void {
    if (!this.selectable) return;
    this.selectionToggle.emit(this.widgetPreset);
  }

  onVizTypeClick(vizType: WidgetVizType, event: Event): void {
    if (!this.selectable) {
      // legacy behavior — viz click triggers preview emission
      this.previewWidget(vizType);
      return;
    }
    // selectable mode — viz click toggles its individual selection;
    // stop propagation so it doesn't also fire the card-level toggle.
    event.stopPropagation();
    this.vizTypeToggle.emit({
      widgetPreset: this.widgetPreset,
      vizType: vizType,
    });
  }

  isVizTypeSelected(vizTypeId: number): boolean {
    if (this.selectable) {
      return this.selectedVizTypeIds?.includes(vizTypeId) ?? false;
    }
    // legacy single-select highlight
    return (
      this.widgetPreset.id === this.selectedPresetId &&
      vizTypeId === this.selectedChartTypeId
    );
  }
}
